import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ChevronLeft, Users, FileText, Tag, Settings, BarChart3, Loader2, Activity, Mail, MessageCircle, Layout, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { AdminDashboardTab } from '../components/admin/AdminDashboardTab';
import { AdminReportsTab } from '../components/admin/AdminReportsTab';
import { AdminRefundQueueTab } from '../components/admin/AdminRefundQueueTab';
import { AdminCouponsTab } from '../components/admin/AdminCouponsTab';
import { AdminSettingsTab } from '../components/admin/AdminSettingsTab';
import { AdminSubmissionsTab } from '../components/admin/AdminSubmissionsTab';
import { AdminSubmissionModal } from '../components/admin/AdminSubmissionModal';
import { AdminAnalyticsTab } from '../components/admin/AdminAnalyticsTab';
import { AdminEmailAutomationTab } from '../components/admin/AdminEmailAutomationTab';
import { AdminTestimonialsTab } from '../components/admin/AdminTestimonialsTab';
import { AdminLandingTab } from '../components/admin/AdminLandingTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Admin = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');

  // Data state
  const [submissions, setSubmissions] = useState([]);
  const [reports, setReports] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [refundPendingCount, setRefundPendingCount] = useState(0);

  // Action state
  const [resendingId, setResendingId] = useState(null);
  const [generatingForId, setGeneratingForId] = useState(null);

  // Coupon form
  const [newCoupon, setNewCoupon] = useState({ code: '', type: 'percentage', value: '', expiry_date: '', min_order_value: '', usage_limit: '', per_user_limit: '1' });
  const [isCreatingCoupon, setIsCreatingCoupon] = useState(false);

  // Settings state
  const [reportPriceInr, setReportPriceInr] = useState('');
  const [isSavingPrice, setIsSavingPrice] = useState(false);
  const [basePriceInr, setBasePriceInr] = useState('');
  const [isSavingBasePrice, setIsSavingBasePrice] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);

  // Date filter
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    if (isAuthenticated) {
      loadSubmissions(); loadReports(); loadCoupons(); loadAnalytics(); loadReportPrice(); loadRefundCount();
      // Poll submissions, reports, analytics, refund count every 30s for live counts
      const interval = setInterval(() => {
        // Silent polling — no toasts on error
        fetch(`${API_URL}/api/admin/submissions`).then(r => r.json()).then(d => setSubmissions(d.submissions || [])).catch(() => {});
        fetch(`${API_URL}/api/admin/reports`).then(r => r.json()).then(d => setReports(d.reports || [])).catch(() => {});
        fetch(`${API_URL}/api/admin/analytics`).then(r => r.json()).then(setAnalytics).catch(() => {});
        fetch(`${API_URL}/api/admin/refund-queue?limit=1`).then(r => r.json()).then(d => setRefundPendingCount(d.pending ?? 0)).catch(() => {});
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  const loadSubmissions = async () => {
    try {
      const data = await fetch(`${API_URL}/api/admin/submissions`).then(r => r.json());
      setSubmissions(data.submissions || []);
    } catch { toast.error('Failed to load submissions'); }
  };

  const loadReports = async () => {
    try {
      const data = await fetch(`${API_URL}/api/admin/reports`).then(r => r.json());
      setReports(data.reports || []);
    } catch { console.error('Failed to load reports'); }
  };

  const loadRefundCount = async () => {
    try {
      const data = await fetch(`${API_URL}/api/admin/refund-queue?limit=1`).then(r => r.json());
      setRefundPendingCount(data.pending ?? 0);
    } catch { /* silent */ }
  };

  const loadCoupons = async () => {
    try {
      const data = await fetch(`${API_URL}/api/admin/coupons`).then(r => r.json());
      setCoupons(data.coupons || []);
    } catch { console.error('Failed to load coupons'); }
  };

  const loadAnalytics = async () => {
    try {
      const data = await fetch(`${API_URL}/api/admin/analytics`).then(r => r.json());
      setAnalytics(data);
    } catch { console.error('Failed to load analytics'); }
  };

  const loadReportPrice = async () => {
    try {
      const data = await fetch(`${API_URL}/api/report-price`).then(r => r.json());
      if (data.price_inr !== undefined && data.price_inr !== null) setReportPriceInr(String(data.price_inr));
      if (data.base_price_inr !== undefined && data.base_price_inr !== null) setBasePriceInr(String(data.base_price_inr));
      else setBasePriceInr('');
    } catch { console.error('Failed to load report price'); }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);
    try {
      const data = await fetch(`${API_URL}/api/admin/login`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password }) }).then(r => r.json());
      if (data.success) { setIsAuthenticated(true); toast.success('Logged in successfully'); }
      else toast.error(data.message || 'Invalid password');
    } catch { toast.error('Login failed. Please try again.'); }
    finally { setIsLoggingIn(false); }
  };

  const handleSavePrice = async (e) => {
    e.preventDefault();
    if (!reportPriceInr || parseFloat(reportPriceInr) < 0) { toast.error('Please enter a valid price'); return; }
    setIsSavingPrice(true);
    try {
      const data = await fetch(`${API_URL}/api/admin/report-price`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ price_inr: parseFloat(reportPriceInr) }) }).then(r => r.json());
      if (data.success) toast.success(`Report price updated to ₹${parseFloat(reportPriceInr).toLocaleString()}`);
      else toast.error(data.detail || 'Failed to update price');
    } catch { toast.error('Failed to update price'); }
    finally { setIsSavingPrice(false); }
  };

  const handleSaveBasePrice = async (e) => {
    e.preventDefault();
    const val = basePriceInr === '' ? null : parseFloat(basePriceInr);
    if (val !== null && (isNaN(val) || val < 0)) { toast.error('Please enter a valid marketing price'); return; }
    setIsSavingBasePrice(true);
    try {
      const data = await fetch(`${API_URL}/api/admin/report-base-price`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ base_price_inr: val }) }).then(r => r.json());
      if (data.success) toast.success(val ? `Anchor price updated to ₹${val.toLocaleString()}` : 'Anchor price cleared');
      else toast.error(data.detail || 'Failed to update base price');
    } catch { toast.error('Failed to update base price'); }
    finally { setIsSavingBasePrice(false); }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    if (newPassword.length < 4) { toast.error('Password must be at least 4 characters'); return; }
    setIsChangingPassword(true);
    try {
      const data = await fetch(`${API_URL}/api/admin/change-password`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }) }).then(r => r.json());
      if (data.success) { toast.success(data.message); setCurrentPassword(''); setNewPassword(''); setConfirmPassword(''); }
      else toast.error(data.message || 'Failed to change password');
    } catch { toast.error('Failed to change password'); }
    finally { setIsChangingPassword(false); }
  };

  const handleCreateCoupon = async (e) => {
    e.preventDefault();
    if (!newCoupon.code || !newCoupon.value || !newCoupon.expiry_date) { toast.error('Code, value, and expiry date are required'); return; }
    setIsCreatingCoupon(true);
    try {
      const data = await fetch(`${API_URL}/api/admin/coupons`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ code: newCoupon.code, type: newCoupon.type, value: parseFloat(newCoupon.value), expiry_date: newCoupon.expiry_date, min_order_value: newCoupon.min_order_value ? parseInt(newCoupon.min_order_value) * 100 : 0, usage_limit: newCoupon.usage_limit ? parseInt(newCoupon.usage_limit) : 0, per_user_limit: newCoupon.per_user_limit ? parseInt(newCoupon.per_user_limit) : 1 }) }).then(r => r.json());
      if (data.success) { toast.success(`Coupon ${newCoupon.code.toUpperCase()} created!`); setNewCoupon({ code: '', type: 'percentage', value: '', expiry_date: '', min_order_value: '', usage_limit: '', per_user_limit: '1' }); loadCoupons(); }
      else toast.error(data.detail || 'Failed to create coupon');
    } catch { toast.error('Failed to create coupon'); }
    finally { setIsCreatingCoupon(false); }
  };

  const handleDeleteCoupon = async (code) => {
    if (!window.confirm(`Delete coupon ${code}?`)) return;
    try { await fetch(`${API_URL}/api/admin/coupons/${code}`, { method: 'DELETE' }); toast.success(`Coupon ${code} deleted`); loadCoupons(); }
    catch { toast.error('Failed to delete coupon'); }
  };

  const handleToggleCoupon = async (code) => {
    try {
      const data = await fetch(`${API_URL}/api/admin/coupons/${code}/toggle`, { method: 'PATCH' }).then(r => r.json());
      if (data.success) { toast.success(`Coupon ${code} ${data.active ? 'activated' : 'deactivated'}`); loadCoupons(); }
    } catch { toast.error('Failed to toggle coupon'); }
  };

  const handleDownloadReport = async (reportId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/reports/${reportId}/download`);
      if (!response.ok) throw new Error();
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `Report_${reportId.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); window.URL.revokeObjectURL(url); document.body.removeChild(a);
      toast.success('Report downloaded');
    } catch { toast.error('Failed to download report'); }
  };

  const handleResendReport = async (reportId, email) => {
    setResendingId(reportId);
    try {
      const data = await fetch(`${API_URL}/api/admin/reports/${reportId}/resend`, { method: 'POST', headers: { 'Content-Type': 'application/json' } }).then(r => r.json());
      if (data.success) { toast.success(`Report sent to ${email}`); loadReports(); }
      else toast.error(data.message || 'Failed to send report');
    } catch { toast.error('Failed to send report'); }
    finally { setResendingId(null); }
  };

  const handleAdminGenerateReport = async (submission) => {
    if (!window.confirm(`Generate premium report and email it to ${submission.user_data?.email}?`)) return;
    setGeneratingForId(submission.id);
    try {
      const res = await fetch(`${API_URL}/api/admin/generate-send-report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_data: submission.user_data,
          quiz_results: submission.results,
          visual_vibes: submission.visual_vibes || [],
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        toast.success(`Report generated for ${submission.user_data?.email}. Email is being sent in the background.`);
        loadReports();
      } else {
        toast.error(data.detail || data.email_message || 'Failed to generate report');
      }
    } catch { toast.error('Network error while generating report'); }
    finally { setGeneratingForId(null); }
  };

  const handleDownloadPaidReport = (email) => {
    const report = reports.filter(r => r.report_type === 'premium').find(r => r.user_email?.toLowerCase() === email?.toLowerCase());
    if (!report) { toast.error('Report not found'); return; }
    window.open(`${API_URL}/api/admin/reports/${report.id}/download`, '_blank');
  };

  const formatDate = (d) => new Date(d).toLocaleString();

  const paidEmails = new Set(reports.filter(r => r.report_type === 'premium').map(r => r.user_email?.toLowerCase()));

  const filteredSubmissions = submissions.filter((s) => {
    const d = new Date(s.submitted_at);
    if (dateFrom) { const f = new Date(dateFrom); f.setHours(0,0,0,0); if (d < f) return false; }
    if (dateTo) { const t = new Date(dateTo); t.setHours(23,59,59,999); if (d > t) return false; }
    return true;
  });

  const downloadCSV = () => {
    const headers = ['Name','Company','Email','Date','Status','Primary Archetype','Secondary Archetype'];
    const rows = filteredSubmissions.map((s) => {
      const email = (s.user_data?.email || '').toLowerCase();
      return [`"${(s.user_data?.name||'').replace(/"/g,'""')}"`,`"${(s.user_data?.company_name||'').replace(/"/g,'""')}"`,`"${email}"`,s.submitted_at ? new Date(s.submitted_at).toLocaleDateString() : '',paidEmails.has(email)?'Paid':'Unpaid',s.results?.dominant||'',s.results?.secondary||''].join(',');
    });
    const blob = new Blob([[headers.join(','),...rows].join('\n')], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `submissions${dateFrom||dateTo?`_${dateFrom||'start'}_to_${dateTo||'now'}`:''}.csv`;
    a.click(); URL.revokeObjectURL(a.href);
    toast.success(`Downloaded ${filteredSubmissions.length} records`);
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex flex-col bg-black">
        <Header />
        <main className="flex-1 bg-black flex items-center justify-center px-6 pb-16">
          <Card className="w-full max-w-md bg-[#0a0a0a] border-gray-800">
            <CardHeader><CardTitle className="text-2xl text-center text-gray-200">Admin Login</CardTitle></CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter admin password" className="mt-2" />
                </div>
                <div className="flex gap-4">
                  <Button type="button" variant="outline" onClick={() => navigate('/')} className="flex-1">Back</Button>
                  <Button type="submit" disabled={isLoggingIn} style={{ background: 'linear-gradient(to right, #7A00EB, #00c3ef)' }} className="flex-1 text-white hover:opacity-90">
                    {isLoggingIn ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Login
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  const tabs = [
    { key: 'dashboard', label: 'Dashboard', icon: <BarChart3 className="h-4 w-4 mr-2" />, testId: 'dashboard-tab-btn' },
    { key: 'analytics', label: 'Analytics', icon: <Activity className="h-4 w-4 mr-2" />, testId: 'analytics-tab-btn' },
    { key: 'submissions', label: `Submissions (${submissions.length})`, icon: <Users className="h-4 w-4 mr-2" /> },
    { key: 'reports', label: `Generated Reports (${reports.length})`, icon: <FileText className="h-4 w-4 mr-2" /> },
    { key: 'refund-queue', label: `Refund Queue${refundPendingCount ? ` (${refundPendingCount})` : ''}`, icon: <AlertTriangle className="h-4 w-4 mr-2" />, testId: 'refund-queue-tab-btn' },
    { key: 'coupons', label: `Coupons (${coupons.length})`, icon: <Tag className="h-4 w-4 mr-2" />, testId: 'coupons-tab-btn' },
    { key: 'email-automation', label: 'Email Automation', icon: <Mail className="h-4 w-4 mr-2" />, testId: 'email-automation-tab-btn' },
    { key: 'testimonials', label: 'Testimonials', icon: <MessageCircle className="h-4 w-4 mr-2" />, testId: 'testimonials-tab-btn' },
    { key: 'landing', label: 'Landing Page', icon: <Layout className="h-4 w-4 mr-2" />, testId: 'landing-tab-btn' },
    { key: 'settings', label: 'Settings', icon: <Settings className="h-4 w-4 mr-2" />, testId: 'settings-tab-btn' },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-black">
      <Header />
      <main className="flex-1 bg-black pb-16">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/')} className="text-gray-300 hover:text-gray-100 hover:bg-gray-900"><ChevronLeft className="h-4 w-4 mr-1" />Back to Home</Button>
              <h1 className="text-2xl font-bold text-gray-200">Admin Dashboard</h1>
            </div>
            <Button variant="outline" onClick={() => setIsAuthenticated(false)} className="border-gray-700 text-gray-300 hover:bg-gray-900 hover:text-gray-100">Logout</Button>
          </div>

          <div className="flex flex-wrap gap-3 mb-6">
            {tabs.map((tab) => (
              <Button
                key={tab.key}
                variant={activeTab === tab.key ? 'default' : 'outline'}
                onClick={() => setActiveTab(tab.key)}
                data-testid={tab.testId}
                className={activeTab === tab.key
                  ? 'bg-purple-700 hover:bg-purple-600 text-white'
                  : 'border-gray-700 bg-transparent text-gray-300 hover:bg-gray-900 hover:text-gray-100'}
              >
                {tab.icon}{tab.label}
              </Button>
            ))}
          </div>

          {activeTab === 'dashboard' && <AdminDashboardTab analytics={analytics} />}
          {activeTab === 'analytics' && <AdminAnalyticsTab />}
          {activeTab === 'reports' && <AdminReportsTab reports={reports} handleDownloadReport={handleDownloadReport} handleResendReport={handleResendReport} resendingId={resendingId} formatDate={formatDate} />}
          {activeTab === 'refund-queue' && <AdminRefundQueueTab formatDate={formatDate} />}
          {activeTab === 'coupons' && <AdminCouponsTab coupons={coupons} newCoupon={newCoupon} setNewCoupon={setNewCoupon} isCreatingCoupon={isCreatingCoupon} handleCreateCoupon={handleCreateCoupon} handleDeleteCoupon={handleDeleteCoupon} handleToggleCoupon={handleToggleCoupon} />}
          {activeTab === 'email-automation' && <AdminEmailAutomationTab />}
          {activeTab === 'testimonials' && <AdminTestimonialsTab />}
          {activeTab === 'landing' && <AdminLandingTab />}
          {activeTab === 'settings' && <AdminSettingsTab reportPriceInr={reportPriceInr} setReportPriceInr={setReportPriceInr} isSavingPrice={isSavingPrice} handleSavePrice={handleSavePrice} basePriceInr={basePriceInr} setBasePriceInr={setBasePriceInr} isSavingBasePrice={isSavingBasePrice} handleSaveBasePrice={handleSaveBasePrice} currentPassword={currentPassword} setCurrentPassword={setCurrentPassword} newPassword={newPassword} setNewPassword={setNewPassword} confirmPassword={confirmPassword} setConfirmPassword={setConfirmPassword} isChangingPassword={isChangingPassword} showCurrentPw={showCurrentPw} setShowCurrentPw={setShowCurrentPw} showNewPw={showNewPw} setShowNewPw={setShowNewPw} handleChangePassword={handleChangePassword} loadSubmissions={loadSubmissions} loadReports={loadReports} loadAnalytics={loadAnalytics} />}
          {activeTab === 'submissions' && <AdminSubmissionsTab filteredSubmissions={filteredSubmissions} submissions={submissions} dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo} downloadCSV={downloadCSV} paidEmails={paidEmails} setSelectedSubmission={setSelectedSubmission} generatingForId={generatingForId} handleAdminGenerateReport={handleAdminGenerateReport} handleDownloadPaidReport={handleDownloadPaidReport} formatDate={formatDate} />}

          <AdminSubmissionModal selectedSubmission={selectedSubmission} setSelectedSubmission={setSelectedSubmission} paidEmails={paidEmails} generatingForId={generatingForId} handleAdminGenerateReport={handleAdminGenerateReport} handleDownloadPaidReport={handleDownloadPaidReport} formatDate={formatDate} />
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Admin;
